import { defineComponent as _defineComponent } from 'vue'
import '@/components/pages/settings/UserManagementPage/UserManagementPage.scss';

import { ref, computed, onBeforeMount, reactive, watch } from 'vue';
import store from '@/store';
import { dispatchLoadCompanyUsers } from '@/store/dispatchers/companyDispatchers';
import apiService from '@/services/ApiService';
import authService from '@/services/AuthService';
import notificationService from '@/services/NotificationService';
import { Logger } from '@/other/Logger';
import { CompanyUserStatus } from '@/@types/enums/CompanyUserStatus';
import { CompanyUserStatusAction } from '@/@types/enums/CompanyUserStatusAction';
import { AnyJson } from '@/@types/AnyJson';
import { User } from '@/models/User';
import { Company } from '@/models/Company';
import { Loading } from 'element-ui';
import { HOME } from '@/routerNames';
import router from '@/router';

// Components
import SelectRoleDialog from '@/components/layout/dialogs/SelectRoleDialog.vue';
import UserCreateDialog from '@/components/layout/dialogs/UserCreateDialog.vue';
import UserInviteDialog from '@/components/pages/settings/UserManagementPage/UserInviteDialog.vue';
import UserProfilePhoto from '@/components/layout/shared/ProfilePhoto/UserProfilePhoto.vue';
import UserDeleteConfirmDialog from './UserDeleteConfirmDialog.vue';
import AccountRecoveryDialog from './AccountRecoveryDialog.vue';
import { canStartAccountRecovery, hasMembershipOutside } from '@/services/accountRecoveryRules';

interface IWorkspaceAdminInfo {
  id: string;
  title: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'UserManagementPage',
  setup(__props) {

const log = new Logger('UserManagementPage');

const userCreateDialog = ref<InstanceType<typeof UserCreateDialog>>();
const userInviteDialog = ref<InstanceType<typeof UserInviteDialog>>();
const userDeleteDialog = ref<InstanceType<typeof UserDeleteConfirmDialog>>();
const accountRecoveryDialog = ref<InstanceType<typeof AccountRecoveryDialog>>();
const userToDelete = ref<User | null>(null);
const roleDialogUser = ref<User | null>(null);
const soleAdminWorkspaces = ref<IWorkspaceAdminInfo[]>([]);

const searchQuery = ref('');
const users = ref<User[]>([]);
const invites = ref<AnyJson[]>([]);
const showInvited = ref(true);
const showActive = ref(true);
const filteredUsers = ref<(User | AnyJson)[]>([]);
const updateFilteredUsersTimeout = ref(0);

const loader = ref<any>(Loading);

const userRoleFilter = reactive({
  owner: true,
  moderator: true,
  user: true,
  admin: true,
});

const user = computed((): User => store.state.user);
const company = computed((): Company => store.state.company);

async function withLoader<T>(cb: () => Promise<T>): Promise<T | undefined> {
  try {
    loader.value = Loading.service({});
    return await cb();
  } finally {
    loader.value?.close();
  }
}

const filterUsersByQuery = (userList: (User | AnyJson)[]): (User | AnyJson)[] => {
  return userList.filter((item) => {
    const role = item.role as 'owner' | 'admin' | 'moderator' | 'user';
    const userString = `${item.firstName}${item.lastName}${item.email}`.toLowerCase().replace(/\s/g, '');
    const searchTerm = searchQuery.value.toLowerCase().replace(/\s/g, '');
    const matchesSearch = userString.includes(searchTerm);
    const matchesRole = userRoleFilter[role] || (!role && userRoleFilter['user']);
    return matchesSearch && matchesRole;
  });
};

const updateFilteredUsers = () => {
  let results: (User | AnyJson)[] = [];

  if (showActive.value) {
    results = [...results, ...filterUsersByQuery(users.value)];
  }
  if (showInvited.value) {
    results = [...results, ...filterUsersByQuery(invites.value)];
  }

  filteredUsers.value = results;
};

// SNAP-2256: the @-mention list (and comment authors) read store.state.companyUsers, which is
// otherwise loaded once per session and never invalidated. Every change made on this page has to
// re-read /company/users into the store, or a user deleted here keeps being offered in the editor
// until the page is reloaded.
const refreshCompanyUsers = async () => {
  try {
    await dispatchLoadCompanyUsers();
    users.value = [...store.state.companyUsers].sort((a, b) => a.getName().localeCompare(b.getName()));
  } catch (err) {
    apiService.handleResponseError(err);
  }

  updateFilteredUsers();
};

const updateFilteredUsersWithTimeout = () => {
  window.clearTimeout(updateFilteredUsersTimeout.value);
  updateFilteredUsersTimeout.value = window.setTimeout(() => {
    window.requestAnimationFrame(updateFilteredUsers);
  }, 200);
};

const isUserActive = (user: User): boolean => !!user.groups;

const getUserStatus = (user: User): CompanyUserStatus | undefined => {
  if (!company.value) return undefined;

  const userStatus = company.value.getUserStatus(user.id);
  const validStatuses = Object.values(CompanyUserStatus);

  if (validStatuses.includes(userStatus)) {
    return userStatus;
  }

  return isUserActive(user) ? CompanyUserStatus.ACTIVE : CompanyUserStatus.INVITED;
};

const getUserStatusAction = (user: User): string => {
  const status = getUserStatus(user);
  if (status === CompanyUserStatus.ACTIVE) return CompanyUserStatusAction.DEACTIVATE;
  if (status === CompanyUserStatus.DEACTIVATED) return CompanyUserStatusAction.ACTIVATE;
  return '';
};

const userRoleFormat = (role: string): string => {
  const roleMap: { [key: string]: string } = {
    owner: 'Owner',
    admin: 'Administrator',
    moderator: 'User Manager',
    user: 'Team Member',
  };
  return roleMap[role] || role;
};

const changeUserStatus = async (user: User) => {
  const currentStatus = getUserStatus(user);
  const newStatus =
    currentStatus === CompanyUserStatus.ACTIVE ? CompanyUserStatus.DEACTIVATED : CompanyUserStatus.ACTIVE;

  try {
    await apiService.changeUserStatus(user.id, newStatus);
    company.value.setUserStatus(user.id, newStatus);
    await refreshCompanyUsers();
    const message = newStatus === CompanyUserStatus.ACTIVE ? 'activated' : 'deactivated';
    notificationService.success(`The user is ${message} successfully`);
  } catch (err) {
    apiService.handleResponseError(err);
  }
};

const handleInviteUser = () => {
  userInviteDialog.value?.show();
};

// SNAP-2424: the server makes the same decision in POST /company/user/:id/account-recovery.
const canRecoverAccount = (target: User | AnyJson): boolean => {
  const actor = user.value;
  if (!actor || !target || !target.id) return false;

  return canStartAccountRecovery(
    { id: actor.id, role: actor.role, isSuperAdmin: (actor.globalRoles ?? []).includes('admin') },
    {
      id: target.id,
      role: target.role,
      isBot: (target.globalRoles ?? []).includes('bot'),
      isSuperAdmin: (target.globalRoles ?? []).includes('admin'),
      hasOtherCompanies: hasMembershipOutside(target, company.value?.id ?? ''),
    }
  );
};

const handleRecoverAccount = (target: User) => {
  accountRecoveryDialog.value?.show(target, userRoleFormat(target.role));
};

const handleInvited = async () => {
  try {
    const res = await apiService.getCompanyInvitedUsers();
    invites.value = res.data.items ?? [];
    updateFilteredUsers();
  } catch (err) {
    apiService.handleResponseError(err);
  }
};

const handleCreateUser = async () => {
  try {
    const companyId =
      typeof authService.user.company === 'string' ? authService.user.company : authService.user.company.id;

    const createdUser = await userCreateDialog.value?.show({ company: companyId });

    if (createdUser) {
      await refreshCompanyUsers();
    }
  } catch (err) {
    if (err) log.error(err);
  }
};

const handleSaveUserRole = async (role: string, user: User) => {
  await withLoader(async () => {
    await apiService.changeCompanyRole(user.id, role);
    user.role = role;
    await refreshCompanyUsers();
  });
};

const handleChangeStatus = (user: User) => {
  changeUserStatus(user);
};

const removeInvitation = async (item: AnyJson) => {
  try {
    await apiService.removeInvitation(item._id);
    const index = invites.value.indexOf(item);
    if (index >= 0) invites.value.splice(index, 1);
    updateFilteredUsers();
    notificationService.success('Invitation successfully removed');
  } catch (err) {
    apiService.handleResponseError(err);
  }
};

const removeUser = async (user: User) => {
  // Owners cannot be deleted
  if (user.role === 'owner') return;

  // If not active, remove invitation instead
  if (!isUserActive(user)) {
    await removeInvitation(user);
    return;
  }

  try {
    userToDelete.value = user;
    await apiService.removeCompanyUser(user.id);
    await refreshCompanyUsers();
    notificationService.success('Account successfully deleted');
  } catch (err: any) {
    // Handle sole admin case or articles exist - show dialog for transfer options
    if (
      err.response?.status === 400 &&
      (err.response?.data?.soleAdminWorkspaces || err.response?.data?.articlesCount)
    ) {
      soleAdminWorkspaces.value = err.response.data.soleAdminWorkspaces ?? [];
      const articlesCount = err.response.data.sharedArticlesIds ?? 0;

      try {
        const usersRes = await apiService.company.getUsers();
        const users = usersRes.data.users ?? [];
        const availableUsers = users.filter((u) => u.id !== user.id);
        userDeleteDialog.value?.show(availableUsers, articlesCount);
      } catch (fetchErr) {
        apiService.handleResponseError(fetchErr);
      }
    } else {
      apiService.handleResponseError(err);
    }
  }
};

const removeUserConfirmed = async (data: { transferToUserId?: string; deleteAll: boolean }) => {
  if (!userToDelete.value) return;

  try {
    await apiService.removeCompanyUser(userToDelete.value.id, data);
    await refreshCompanyUsers();
    notificationService.success('Account successfully deleted');
  } catch (err) {
    apiService.handleResponseError(err);
  } finally {
    userToDelete.value = null;
  }
};

const loadUsers = async () => {
  await withLoader(async () => {
    const isActive = await authService.isUserActive();
    if (!isActive) {
      await router.push({ name: HOME });
      return;
    }

    await refreshCompanyUsers();

    try {
      const invitesResp = await apiService.getCompanyInvitedUsers();
      invites.value = invitesResp.data.items ?? [];
    } catch (err) {
      apiService.handleResponseError(err);
    }
  });
};

onBeforeMount(async () => {
  await loadUsers();
  updateFilteredUsers();
});

watch(
  [() => searchQuery.value, () => showActive.value, () => showInvited.value, () => userRoleFilter],
  () => updateFilteredUsersWithTimeout(),
  { deep: true }
);

return { __sfc: true,log, userCreateDialog, userInviteDialog, userDeleteDialog, accountRecoveryDialog, userToDelete, roleDialogUser, soleAdminWorkspaces, searchQuery, users, invites, showInvited, showActive, filteredUsers, updateFilteredUsersTimeout, loader, userRoleFilter, user, company, withLoader, filterUsersByQuery, updateFilteredUsers, refreshCompanyUsers, updateFilteredUsersWithTimeout, isUserActive, getUserStatus, getUserStatusAction, userRoleFormat, changeUserStatus, handleInviteUser, canRecoverAccount, handleRecoverAccount, handleInvited, handleCreateUser, handleSaveUserRole, handleChangeStatus, removeInvitation, removeUser, removeUserConfirmed, loadUsers, SelectRoleDialog, UserCreateDialog, UserInviteDialog, UserProfilePhoto, UserDeleteConfirmDialog, AccountRecoveryDialog }
}

})